import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/DateInput";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { paymentsApi, docBalance, PAYMENT_METHODS } from "@/api";
import { useLang, fmtMoney } from "@/i18n";

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
    {children}
  </div>
);

export const PaymentDialog = ({ open, onOpenChange, doc, onSaved }) => {
  const { t } = useLang();
  const balance = docBalance(doc);
  const [form, setForm] = useState({ amount: "", date: "", method: "bank", reference: "", notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ amount: balance.toFixed(2), date: new Date().toISOString().slice(0, 10), method: doc.payment_method || "bank", reference: "", notes: "" });
  }, [open, balance, doc.payment_method]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    const amount = Number(form.amount);
    if (!amount || amount <= 0) return toast.error(`${t("amount")}: ${t("required")}`);
    if (amount > balance + 0.005) return toast.error(t("amount_exceeds"));
    setSaving(true);
    try {
      const res = await paymentsApi.create(doc.id, { ...form, amount });
      toast.success(t("payment_saved"));
      onSaved(res.document);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.status === 400 ? t("amount_exceeds") : t("error"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{t("add_payment")}</DialogTitle>
          <DialogDescription className="font-mono">{doc.number} · {t("balance")}: {fmtMoney(balance)}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label={t("amount")}>
              <div className="flex gap-2">
                <Input type="number" step="0.01" min="0.01" value={form.amount} onChange={set("amount")} className="font-mono" data-testid="payment-amount-input" autoFocus />
              </div>
            </Field>
            <Field label={t("date")}>
              <DateInput value={form.date} onChange={set("date")} className="font-mono" data-testid="payment-date-input" />
            </Field>
          </div>
          <button type="button" onClick={() => set("amount")(balance.toFixed(2))} className="text-xs text-blue-700 dark:text-blue-300 hover:underline" data-testid="payment-full-balance-button">{t("pay_full")} ({fmtMoney(balance)})</button>
          <Field label={t("payment_method")}>
            <Select value={form.method} onValueChange={set("method")}>
              <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{t(`pay_${m}`)}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={t("reference")}>
            <Input value={form.reference} onChange={set("reference")} className="font-mono" placeholder="ΑΠΥ-0001" data-testid="payment-reference-input" />
          </Field>
          <Field label={t("notes")}>
            <Input value={form.notes} onChange={set("notes")} data-testid="payment-notes-input" />
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="payment-cancel-button">{t("cancel")}</Button>
            <Button type="submit" disabled={saving} className="bg-blue-800 hover:bg-blue-700" data-testid="payment-submit-button">{t("save")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
